import {AuthError, EventType, InteractionRequiredAuthError} from "@azure/msal-browser";
import {useMsal} from "@azure/msal-react";
import {User} from "./user-model";
import {useCallback, useEffect, useState} from "react";
import {SingInOptions} from "./auth-types";
import {apiWrapper} from "../../config/api-wrapper";
import AuthContext from "./auth-context";
import msEntraAuthService from "./auth.service";
import {loginRequest} from "../../config/ms-entra-auth";

export default function AuthProvider({children}: { children: React.ReactNode }) {
    const [user, setUser] = useState<User | undefined>();
    const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
    const {instance, accounts} = useMsal();

    const signIn = useCallback(async (options: SingInOptions): Promise<void> => {
        try{
            await instance.loginPopup(loginRequest);
        }catch (error) {
            if((error as AuthError).errorCode === "popup_blocked"){
                alert("Please allow popups for this site");
            }else{
                console.error('LoginPopup error:', error)
            }
        }
    }, [instance])

    const signOut = useCallback((): void => {
        msEntraAuthService.setLoggedUser(null)
        setUser(undefined)
        setIsAuthenticated(false)
        void instance.logoutPopup();
    }, [instance]);

    const handleAuthFailure = useCallback(() => {
        setIsAuthenticated(false);
        setUser(undefined);
        msEntraAuthService.setLoggedUser(null);
        apiWrapper.clearEntra();
        msEntraAuthService.msalAuthSate = null;
    }, []);

    const onMsEntraAuthChanges = useCallback(async () => {
        try {
            if (accounts.length < 1) throw new Error('No accounts found');

            const token = await instance.acquireTokenSilent({
                account: accounts[0], ...loginRequest
            })

            const accessToken = token.accessToken;
            const idToken = token.idToken;
            const nonce = accounts[0].idTokenClaims?.nonce;

            //Configure tokens on apiWrapper
            apiWrapper.setEntra(
                accessToken, idToken, nonce
            )

            msEntraAuthService.msalAuthSate = {
                accessToken,
                idToken,
                nonce,
            }

            const claims = accounts[0].idTokenClaims

            if (!claims || typeof claims.email !== 'string') {
                console.warn('No valid claims found in user');
                return;
            }

            const userInfo: User = {
                id: accounts[0].localAccountId,
                name: accounts[0].name as string,
                email: claims.email,
            }

            setUser(userInfo)
            setIsAuthenticated(true)
            msEntraAuthService.setLoggedUser(userInfo)

            try {
                await msEntraAuthService.storeUser(
                    accounts[0].localAccountId,
                    claims.email,
                    accounts[0].name)
            } catch (storeError) {
                console.warn('Failed to store user info, but authentication is valid', storeError)
            }
        } catch (error) {
            if (error instanceof InteractionRequiredAuthError) {
                await instance.loginRedirect(loginRequest);
                return;
            }
            console.error('Authentication error:', error)
            handleAuthFailure()
        }
    }, [accounts, instance, handleAuthFailure])

    useEffect(() => {

        const initializeAuth = async () => {
            if (accounts.length > 0) {
                //Check if an MSAL session is active
                await onMsEntraAuthChanges();
            } else {
                // Try to restore the user session
                const storedUser = msEntraAuthService.getLoggedUser();
                const tokensData = localStorage.getItem('authTokens');
        
                if (storedUser && tokensData) {
                    const tokens = JSON.parse(tokensData);

                    // Check token expiration using timestamp
                    const tokenPayload = tokens.accessToken?.split('.')[1];
                    let isValid = false;

                    if (tokenPayload) {
                        try {
                            const decoded = JSON.parse(atob(tokenPayload));
                            if (decoded.exp && Date.now() / 1000 < decoded.exp) {
                                isValid = true;
                            }
                        } catch (e) {
                            isValid = false;
                        }
                    }

                    if (isValid) {
                        apiWrapper.setEntra(tokens.accessToken, tokens.idToken, tokens.nonce);
                        setUser(storedUser);
                        setIsAuthenticated(true);
                        return;
                    }
                }
                // Otherwise, login required
                handleAuthFailure();
            }
        };


        initializeAuth().catch(error => {
            console.error('Failed to initialize auth', error)
            handleAuthFailure()
        })
    }, [accounts, onMsEntraAuthChanges, handleAuthFailure]);

    useEffect(() => {

        const callbackId = instance.addEventCallback((event) => {
            if (
                event.eventType === EventType.LOGIN_SUCCESS ||
                event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS ||
                event.eventType === EventType.HANDLE_REDIRECT_END ||
                event.eventType === EventType.LOGOUT_SUCCESS
            ) {
                console.log('MSAL Auth change event:', event.eventType);
                void onMsEntraAuthChanges();
            }
        })
        return () => {
            console.log('authClient authStateManager unsubscribed');
            if (callbackId !== null) {
                instance.removeEventCallback(callbackId)
            }
        }
    }, [onMsEntraAuthChanges, instance]);
    return (
        <AuthContext.Provider value={{user, isAuthenticated, signIn, signOut}}>
            {children}
        </AuthContext.Provider>
    )
}